/**
 * Entity kind registry — closed enum of LLM-wiki entity kinds, plus per-kind
 * role / embedding-slot / edge-kind enums.
 *
 * Design context: docs/plans/2026-05-26-llm-wiki-entities-prototype-design.md
 *
 * Substrate invariant #5: the DB stores `entities.kind`, `entity_threads.role`,
 * `entity_edges.kind`, and `entity_embeddings.slot` as open `varchar` columns
 * (no CHECK constraints). Zod is the source of truth for which values are
 * valid per kind. New kinds are added here first; the registry in `index.ts`
 * uses `satisfies Record<EntityKind, …>` to make a missing payload schema a
 * compile error.
 */

import { z } from 'zod';

// ── Kinds ────────────────────────────────────────────────────────────

export const entityKindValues = ['person', 'foot_gun'] as const;
export const entityKindSchema = z.enum(entityKindValues);
export type EntityKind = z.infer<typeof entityKindSchema>;

// ── Per-kind role enums (entity_threads.role) ────────────────────────
//
// Role values are open strings in the DB; Zod enumerates the valid set
// per kind. The substrate writer is the only place that validates a
// (kind, role) pair against the matching schema below.

export const personRoleSchema = z.enum([
  'authored',
  'commented',
  'edited',
  'mentioned',
]);
export type PersonRole = z.infer<typeof personRoleSchema>;

export const footGunRoleSchema = z.enum(['observed_in', 'reaffirmed_in']);
export type FootGunRole = z.infer<typeof footGunRoleSchema>;

// ── Per-kind embedding-slot enums (entity_embeddings.slot) ───────────
//
// Person has no embedding slots — Person↔User mirroring doesn't need
// vector retrieval (D10). Zod rejects an empty `z.enum([])`, so we
// express "no slots" as a typed empty tuple at the type level rather
// than as a runtime schema.

export const personSlots = [] as const;
export type PersonSlot = (typeof personSlots)[number];

export const footGunSlotValues = ['primary'] as const;
export const footGunSlotSchema = z.enum(footGunSlotValues);
export type FootGunSlot = z.infer<typeof footGunSlotSchema>;

// ── Edge-kind enum (entity_edges.kind) ───────────────────────────────
//
// Shared across kinds in v1 — `supports` and `supersedes` are the only
// relations the launch kinds need. Per-kind edge-kind enums can be split
// out later if the set diverges.

export const entityEdgeKindValues = ['supports', 'supersedes'] as const;
export const entityEdgeKindSchema = z.enum(entityEdgeKindValues);
export type EntityEdgeKind = z.infer<typeof entityEdgeKindSchema>;
